import {
  AIComponent,
  ALL_MODULES,
  AccessControl,
  AppContext,
  COLOR_MAP,
  COMMON_CONSTANTS,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  DateTimeFormatter,
  FieldTitle,
  PRIORITY_MAP,
  TAB_PAGES,
  TICKET_SOURCE,
  getNotificationTemplateByConstantHook,
  getTranslatedText,
  useIntl,
  useIsAuthorisedHook,
  useUserMSPCheckHook,
  withErrorBoundary,
  InlineEdit,
  useProduct,
  EventEmitter
} from '@nile/components';
import InlineTextField from '@nile/components/src/Components/InlineEdit/Components/InlineTextField';
import '@nile/components/src/Style/ticketDetailsStyles.scss';
import {
  ClientField,
  ConcurrentViewers
} from '@nile/mangifera/src/Components/Ticket';
import ActionsToolbar from '@nile/mangifera/src/Containers/ToolbarContainers/DetailsActionToolbar';
import { TicketContext } from '@nile/mangifera/src/Contexts';
import { getTabId } from '@nile/mangifera/src/Pebbles/TabManager';
import { SVGIcon } from '@superops/super-icons';
import {
  Avatar,
  Button,
  Heading,
  Tooltip as SuperopsTooltip,
  TextStyle,
  Badge,
  Popover,
  ActionList
} from '@superops/ui-design-system';
import React, { useCallback, useContext, useState } from 'react';
import { Tooltip } from 'react-clone-tippy';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import styled from 'styled-components';
import envConfig from '@nile/home/src/Config/env';
import {
  GET_FIELD,
  GET_STATUS_CONFIGURATION,
  AccountClient
} from '@nile/graphql';
import { useQuery } from '@apollo/client';
import { useCopyToClipboard } from '@nile/mangifera/src/Pages/MigrateDataPage/useCopyToClipboard';
import LoadingSuccess from '@nile/components/src/Svg/AnimatedSvg/LoadingSuccess.svg';
import TicketDetailsClientFields from './TicketDetailsClientFields';
import TicketToolbarSla from './TicketToolbarSla';
import { useFlags } from 'launchdarkly-react-client-sdk';
import TicketTypeContainer from '@nile/mangifera/src/Components/Ticket/TicketTypeContainer';
import { AIOnboardingHook } from '@nile/components/src/Components/AI/hooks/AIOnboardingHook';

const TicketBgNew = styled.div`
  width: 34px;
  height: 20px;
  background: ${(props) => props?.background || 'var(--sops-kingly-cloud)'};
  &::before,
  &::after {
    position: absolute;
    content: '';
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
  }

  &::before {
    left: -3px;
  }
  &::after {
    right: -3px;
  }
`;

function StatusChange(props) {
  const { ticket, showConfettiModal, readOnly } = props;
  const intl = useIntl();
  let isFeatureAuthorised = useIsAuthorisedHook('tickets.edit');
  if (typeof readOnly !== 'undefined') {
    isFeatureAuthorised = !readOnly;
  }
  const [popoverActive, setPopoverActive] = useState(false);

  const togglePopoverActive = useCallback(
    () => setPopoverActive((active) => !active),
    []
  );

  const {
    loading: ticketStatusConfigLoading,
    error: statusConfigError,
    data: statusConfigData
  } = useQuery(GET_STATUS_CONFIGURATION, {
    skip: !isFeatureAuthorised,
    fetchPolicy: 'cache-and-network'
  });

  const { data, loading, error } = useQuery(GET_FIELD, {
    client: AccountClient,
    variables: {
      input: {
        module: ALL_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.STATUS
      }
    },
    skip: !isFeatureAuthorised
  });

  const showConfetti = (name) => {
    const statusType = statusConfigData?.getStatusConfiguration.find(
      (el) => el.status.name === name
    )?.configuration;
    showConfettiModal(statusType);
  };

  if (error) return <div>error...</div>;
  const activator = (
    <AccessControl
      roleToCheck="tickets.edit"
      render={
        <Button
          id="statusChange"
          primary
          onClick={!readOnly && togglePopoverActive}
          icon={<SVGIcon name="TicketOutline" color={COLOR_MAP.white} />}
          loading={loading}
        >
          <div className="flex center">
            <TextStyle weight="SemiBold">{ticket.status}</TextStyle>
            {!readOnly && (
              <div className="ml8 fx center">
                <SVGIcon name="ArrowDown" color={COLOR_MAP.white} />
              </div>
            )}
          </div>
        </Button>
      }
      fallback={
        <Button
          id="statusChange"
          primary
          icon={<SVGIcon name="TicketOutline" color={COLOR_MAP.white} />}
        >
          <div className="flex center">
            <TextStyle className="ml8">{ticket.status}</TextStyle>
          </div>
        </Button>
      }
    />
  );

  const changeStatus = (selectedStatus) => {
    props.updateSelection(
      'status',
      selectedStatus.value,
      showConfetti(selectedStatus.value)
    );
    sessionStorage.removeItem('closeTicketClicked');
    sessionStorage.removeItem('suppressCloseNotificationClicked');
    if (selectedStatus.details?.constants === 'CLOSE') {
      sessionStorage.setItem('closeTicketClicked', true);
    }
    togglePopoverActive();
  };

  const changeStatusToClosed = (selectedStatus) => {
    props.updateProperties(
      {
        suppressCloseNotification: true,
        status: selectedStatus.value
      },
      showConfetti(selectedStatus.value)
    );
    sessionStorage.removeItem('closeTicketClicked');
    sessionStorage.setItem('suppressCloseNotificationClicked', true);
    togglePopoverActive();
  };

  const getStatusListContent = (el) => {
    if (el.details?.constants && el.details?.constants.includes('CLOSE')) {
      return (
        <div className="flex actr">
          <div>{el?.value}</div>
          <div
            onClick={(event) => {
              event.stopPropagation();
              changeStatusToClosed(el);
            }}
            className="fx actr mlauto ptr"
          >
            <Tooltip
              html={
                <div className="p3 f12">
                  {getTranslatedText(
                    intl,
                    'ticket.action.closeWithoutNotification'
                  )}
                </div>
              }
              delay={200}
              duration={250}
              position="bottom"
              distance={10}
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
            >
              <Badge
                radial
                status="attention"
                icon={<SVGIcon name="NotificationsBellCrossed" size="sm" />}
              />
            </Tooltip>
          </div>
        </div>
      );
    }
    return <div>{el?.value}</div>;
  };

  const fldOptions = data?.getField?.options || data?.getField?.fieldOptions;
  return (
    <div className="vignesh">
      <Popover
        active={popoverActive}
        activator={activator}
        preferredAlignment="right"
        onClose={togglePopoverActive}
      >
        <ActionList
          actionRole="menuitem"
          items={fldOptions?.map((el) => {
            return {
              content: getStatusListContent(el),
              onAction: () => {
                changeStatus(el);
              }
            };
          })}
        />
      </Popover>
    </div>
  );
}

const FollowersContent = (props) => {
  const { followers } = props;
  return (
    <div className="p10 br-5 flex col g10">
      {followers.map((el) => {
        return (
          <div className="flex center nowrap">
            <div className="mr10">
              <Avatar
                initials={`${el.firstName?.[0] || ''}${el.lastName?.[0] || ''}`}
                size="extraSmall"
              />
            </div>
            <div>
              <FieldTitle className="title">
                {`${el.firstName} ${el.lastName || ''}`}
              </FieldTitle>
            </div>
          </div>
        );
      })}
    </div>
  );
};

function FieldInfo(props) {
  const { icon, action, params } = props;
  return (
    <div className="min-w-200 mr8 nowrap ml-8 max-w-250">
      <TextStyle variation="subdued" weight="Bold">
        {props.label}
      </TextStyle>
      <div className="flex center info mt-8">
        <SVGIcon name={icon} size="sm" />
        <div className="ellipsis">
          {action ? (
            <Link
              fontWeight="bold"
              removeUnderline
              onClick={action ? action.bind(this, params) : ''}
            >
              {props.value || '--'}
            </Link>
          ) : (
            <TextStyle weight="Bold">{props.value || '--'}</TextStyle>
          )}
        </div>
      </div>
    </div>
  );
}

const TicketDetailsHeader = (props) => {
  const {
    ticket,
    saveTicketSubject,
    setSubjectEditState,
    subjectEditState,
    priorityValues,
    updateSelection,
    updateConvertToProject,
    refetchTicket,
    updateProperties,
    showConfettiModal,
    openRequesterSheet,
    concurrentViewers,
    isFormCollapsed,
    readOnly,
    productsToInvoice,
    ticketStatus
  } = props;

  const ticketContext = useContext(TicketContext);
  const isMSP = useUserMSPCheckHook();
  const flags = useFlags();
  const isMSPProduct = useProduct(COMMON_CONSTANTS.MSP);
  const aiOnboardingHook = AIOnboardingHook();
  const [subjectFullWidth, setSubjectFullWidth] = useState(false);

  const intl = useIntl();
  const [isCopied, copyToClipboard] = useCopyToClipboard();

  const getSourceIcon = () => {
    for (const [key, value] of Object.entries(TICKET_SOURCE)) {
      if (value === ticket?.source) {
        return key;
      }
    }
  };
  const isTriageAgent = ticket?.technician?.userId === '14';

  const sourceIcon = getSourceIcon() || 'InlineError';
  const priorityTooltip = getTranslatedText(
    intl,
    'ticket.list.priority.tooltip'
  );
  const sourceToolTip = getTranslatedText(intl, 'ticket.list.source.tooltip');

  const getpriorityImage = (priority) => {
    const priorityId = priorityValues?.find((el) => el.name === priority)?.id;
    return PRIORITY_MAP[priorityId] || 'no_priority';
  };

  const redirectToInvoice = () => {
    ticketContext?.openTab({
      module: TAB_PAGES.newInvoiceFromSource,
      id: ticket?.ticketId,
      data: {
        module: 'TICKET',
        action: 'generateInvoiceFromTicket',
        ticket: ticket,
        productsToInvoice: productsToInvoice,
        updateData: true
      }
    });
  };

  const onSummaryTypeChange = (type) => {
    return {
      variables: {
        input: {
          itemId: ticket?.ticketId,
          itemType: 'TICKET',
          summaryCase: 'OVERALL',
          summaryType: type
        }
      },
      summaryType: type
    };
  };

  const type = getTranslatedText(intl, 'common.type');
  const priorityData = priorityValues?.find(
    (el) => el.name === ticket?.priority
  );

  const getTotalItemCount = () => {
    const productCount = productsToInvoice?.products?.length || 0;
    const worklogCount = productsToInvoice?.worklogs?.length || 0;

    return productCount + worklogCount;
  };

  const totalItemsToInvoice = getTotalItemCount();
  const isTicketEditAuthorised = useIsAuthorisedHook('tickets.edit');
  return (
    <>
      {/* //cm:: todo remove this block */}
      {/* <div>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('TICKET_TRIAGE');
          }}
          aiPrimary
        >
          TICKET_TRIAGE
        </Button>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('RECOMMENDED_SOLUTION');
          }}
          aiPrimary
        >
          RECOMMENDED_SOLUTION
        </Button>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('PROMPT_BUILDER');
          }}
          aiPrimary
        >
          PROMPT_BUILDER
        </Button>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('NESTED_SAMPLE');
          }}
          aiPrimary
        >
          NESTED_SAMPLE
        </Button>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('TICKET_SUMMARIZER');
          }}
          aiPrimary
        >
          TICKET_SUMMARIZER
        </Button>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('WORKLOG_AUTOMATION');
          }}
          aiPrimary
        >
          WORKLOG_AUTOMATION
        </Button>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('SIMILAR_TICKETS');
          }}
          aiPrimary
        >
          SIMILAR_TICKETS
        </Button>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('SCRIPT_GENERATION');
          }}
          aiPrimary
        >
          SCRIPT_GENERATION
        </Button>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('TICKET_EFFICIENCY_ANALYZER');
          }}
          aiPrimary
        >
          TICKET_EFFICIENCY_ANALYZER
        </Button>
        <Button
          onClick={() => {
            EventEmitter.dispatch('showOnboardingUI');
            aiOnboardingHook?.startOnboarding('KB_ASSISTANT');
          }}
          aiPrimary
        >
          KB_ASSISTANT
        </Button>
      </div> */}
      <div
        className="flex full-height full-width p20 bg-white pt10"
        id="ticket-details-header"
      >
        <div className="fx fw fcol fg1">
          <div className="flex mr10 fw actr fw">
            {ticket?.subSource && (
              <div className="fx actr mr10">
                <SVGIcon name={ticket?.subSource} size="lg" />
              </div>
            )}
            <div className="mr15 fg1 fb0">
              <AccessControl
                roleToCheck="tickets.edit"
                render={
                  <div
                    className={
                      ticket?.source === 'Schedule' ||
                      ticket?.source === TICKET_SOURCE.SubscriptionReminder
                        ? 'flex actr position-relative w100'
                        : ''
                    }
                  >
                    {ticket?.source === 'Schedule' && (
                      <Tooltip
                        html={
                          <div className="p4 fsize12">
                            {getTranslatedText(
                              intl,
                              'scheduled.ticket.onhover.tooltip'
                            )}
                          </div>
                        }
                        delay={400}
                        duration={250}
                        position="bottom"
                        inertia
                        trigger="mouseenter"
                        animation="shift"
                        animateFill={false}
                      >
                        <div className="flex actr mr10">
                          <SVGIcon name="DateTime" size="lg" />
                        </div>
                      </Tooltip>
                    )}
                    {ticket?.source === TICKET_SOURCE.SubscriptionReminder && (
                      <Tooltip
                        html={
                          <div className="p4 fsize12">
                            {getTranslatedText(
                              intl,
                              'reminder.ticket.onhover.tooltip'
                            )}
                          </div>
                        }
                        delay={400}
                        duration={250}
                        position="bottom"
                        inertia
                        trigger="mouseenter"
                        animation="shift"
                        animateFill={false}
                      >
                        <div className="fx actr mr10">
                          <SVGIcon name="EstimatedTime" size="lg" />
                        </div>
                      </Tooltip>
                    )}
                    {!readOnly ? (
                      <div className="w100 flex actr">
                        <div
                          id="subject-edit-wrapper"
                          className={subjectFullWidth ? 'w100' : ''}
                        >
                          <InlineTextField
                            defaultValue={ticket.subject}
                            setEditState={(editState) => {
                              setSubjectFullWidth(editState);
                            }}
                            saveValue={(value, cb) => {
                              const afterSave = (
                                saveState,
                                updateTab = true
                              ) => {
                                if (cb) cb(saveState);

                                if (
                                  updateTab &&
                                  ticket?.ticketId &&
                                  ticketContext.updateTab
                                ) {
                                  ticketContext.updateTab(
                                    {
                                      module: 'TICKET',
                                      data: {
                                        ticketId: ticket.ticketId,
                                        subject: value.trim()
                                      }
                                    },
                                    getTabId({
                                      module: 'TICKET',
                                      data: {
                                        ticketId: ticket.ticketId
                                      }
                                    })
                                  );
                                }
                              };

                              saveTicketSubject(value, afterSave);
                            }}
                            setSubjectEditState={(state) => {
                              setSubjectEditState(state);
                            }}
                            ellipsis={false}
                          />
                        </div>
                        {isTriageAgent && (
                          <div>
                            <SVGIcon name="AISparkleAnimated" />
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex g5 actr">
                        <Heading element="h2" weight="Bold">
                          {ticket?.subject}
                        </Heading>
                        {isTriageAgent && (
                          <div>
                            <SVGIcon name="AISparkleAnimated" />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                }
                fallback={
                  <div className="ws-prewrap p5 scroll max-height-110">
                    <div className="flex g5 actr">
                      <Heading element="h2" weight="Bold">
                        {ticket?.subject}
                      </Heading>
                      {isTriageAgent && (
                        <div>
                          <SVGIcon name="AISparkleAnimated" />
                        </div>
                      )}
                    </div>
                  </div>
                }
              />
            </div>
            {!subjectEditState && (
              <div className="mlauto hfc flex actr g10">
                {!readOnly && (
                  <AccessControl
                    roleToCheck="invoice.add"
                    productToCheck="MSP"
                    render={
                      <Tooltip
                        title={getTranslatedText(
                          intl,
                          'client.details.generateInvoice'
                        )}
                        inertia
                        trigger="mouseenter"
                        animation="shift"
                        animateFill={false}
                        className="tooltip"
                        position="bottom"
                      >
                        <Button
                          icon={
                            <div className="flex g5 actr">
                              <div className="flex">
                                <SVGIcon name="InvoiceLogo" />
                              </div>
                              {totalItemsToInvoice > 0 && (
                                <div className="flex">
                                  <Badge status="primary">
                                    {totalItemsToInvoice}
                                  </Badge>
                                </div>
                              )}
                            </div>
                          }
                          onClick={redirectToInvoice}
                          disabled={
                            !productsToInvoice ||
                            (!productsToInvoice?.products &&
                              !productsToInvoice?.worklogs)
                          }
                          id="genereate-invoice"
                        />
                      </Tooltip>
                    }
                  />
                )}

                {!readOnly && (
                  <div id="ticket-toolbar-actions">
                    <Tooltip
                      title={getTranslatedText(intl, 'common.actions')}
                      inertia
                      trigger="mouseenter"
                      animation="shift"
                      animateFill={false}
                      className="tooltip"
                      position="bottom"
                    >
                      <ActionsToolbar
                        ticket={ticket}
                        updateSelection={updateSelection}
                        updateConvertToProject={updateConvertToProject}
                        refetchTicket={refetchTicket}
                        hideText
                        isClassicView={props?.isClassicView}
                        ticketStatus={ticketStatus}
                      />
                    </Tooltip>
                  </div>
                )}

                <div id="ticket-toolbar-status">
                  {((isMSPProduct && ticket?.client) || !isMSPProduct) && (
                    <StatusChange
                      ticket={ticket}
                      updateSelection={updateSelection}
                      updateProperties={updateProperties}
                      refetchTicket={refetchTicket}
                      showConfettiModal={showConfettiModal}
                      readOnly={readOnly}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="flex header-details actr g10 mt10 fx-sb">
            <div className="flex actr wrap" id="left-toolbar-content">
              <div className="flex actr">
                <TicketBgNew
                  className="ticket-icon flex actr jctr mr8 position-relative br-5"
                  background={priorityData?.colorCode}
                >
                  <SVGIcon name={sourceIcon} size="sm" />
                </TicketBgNew>
                <Tooltip
                  title={getTranslatedText(
                    intl,
                    'admin.defaultField.ticketType'
                  )}
                  delay={200}
                  duration={250}
                  position="top"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <div>
                    <InlineEdit
                      editable={isTicketEditAuthorised}
                      defaultValue={ticket?.ticketType}
                      autoSave
                      showProgress
                      editView={({ updateFieldState }) => {
                        return (
                          <div className="fsize12">
                            <TicketTypeContainer
                              currentValue={ticket?.ticketType}
                              onChange={(key, value) => {
                                updateFieldState({
                                  [key]: value
                                });
                              }}
                              showIcon={false}
                            />
                          </div>
                        );
                      }}
                      readView={() => {
                        return (
                          <div className="flex actr g5 p5 visibilityContainer fsize12">
                            <TextStyle>{ticket?.ticketType}</TextStyle>
                            {isTicketEditAuthorised && (
                              <div className="visibilityActions">
                                <div className="fx center">
                                  <SVGIcon name="Edit" />
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      }}
                      onConfirm={(value, callback) => {
                        updateProperties(value, callback);
                      }}
                    />
                  </div>
                </Tooltip>
                <Tooltip
                  html={
                    <>
                      <div className="type">
                        {`${type}: ${ticket?.ticketType ||
                          getTranslatedText(intl, 'app.noData.message')}`}
                      </div>
                      <div className="priority">
                        {`${priorityTooltip} ${ticket?.priority ||
                          getTranslatedText(intl, 'app.noData.message')}`}
                      </div>
                      <div className="source">
                        {`${sourceToolTip} ${ticket?.source ||
                          getTranslatedText(intl, 'app.noData.message')}`}
                      </div>
                    </>
                  }
                  delay={200}
                  duration={250}
                  position="bottom"
                  inertia
                  trigger="mouseenter"
                  animation="shift"
                  animateFill={false}
                >
                  <div className="flex actr g5 fsize12">
                    <TextStyle variation="subdued">
                      <div>ID</div>
                    </TextStyle>
                    <TextStyle weight="Medium">
                      <div>{`#${ticket.displayId}`}</div>
                    </TextStyle>
                  </div>
                </Tooltip>
              </div>

              <Tooltip
                title={getTranslatedText(intl, 'client.new.workingTime.copy')}
                delay={200}
                duration={250}
                position="bottom"
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
              >
                <div
                  className="flex pointer ml-5 mr-20"
                  onClick={() => copyToClipboard(`${ticket.displayId}`)}
                >
                  {isCopied ? (
                    <div className=" flex">
                      <SVGIcon name="LoadingSuccessAnimated" size="sm" />
                    </div>
                  ) : (
                    <SVGIcon name="CopyClipboard" color="#4526FF" size="sm" />
                  )}
                </div>
              </Tooltip>
              {!isMSPProduct || (isMSPProduct && ticket?.client) ? (
                <AccessControl
                  roleToCheck={{
                    operator: 'OR',
                    roles: ['client.view', 'site.view', 'companySite.view']
                  }}
                  render={
                    <div>
                      <TicketDetailsClientFields
                        updateSelection={updateSelection}
                        updateProperties={updateProperties}
                        refetchTicket={refetchTicket}
                        client={ticket?.client}
                        requester={ticket?.requester}
                        department={ticket?.department}
                        site={ticket?.site}
                        openRequesterModal={openRequesterSheet.bind(this, true)}
                        readOnly={readOnly}
                      />
                    </div>
                  }
                  fallback={
                    <>
                      <AccessControl
                        productToCheck={COMMON_CONSTANTS.MSP}
                        render={
                          <FieldInfo
                            label={getTranslatedText(
                              intl,
                              'ticket.details.header.client'
                            )}
                            icon="ClientLogo"
                            value={ticket.client?.name}
                          />
                        }
                      />
                      <AccessControl
                        productToCheck={COMMON_CONSTANTS.IT}
                        render={
                          <div className="flex actr g10">
                            <FieldInfo icon="Site" value={ticket.site?.name} />
                            <FieldInfo
                              icon="Department"
                              value={ticket.department?.name}
                            />
                          </div>
                        }
                      />
                    </>
                  }
                />
              ) : (
                ''
              )}
              <Tooltip
                title={getTranslatedText(intl, 'attribute.addedTime')}
                inertia
                trigger="mouseenter"
                animation="shift"
                animateFill={false}
                className="tooltip"
                position="bottom"
              >
                <div className="flex g5 fsize12 actr">
                  <div className="flex opacity5">
                    <SVGIcon name="ClockRound" size="sm" />
                  </div>
                  <TextStyle weight="Medium">
                    <div>
                      <DateTimeFormatter
                        type="dateTime"
                        date={ticket?.createdTime}
                      />
                    </div>
                  </TextStyle>
                </div>
              </Tooltip>

              {ticket?.sla && isFormCollapsed && isMSP && (
                <div className="flex g5 fsize12 actr" id="ticket-toolbat-sla">
                  <TicketToolbarSla ticket={ticket} />
                </div>
              )}
            </div>
            <div className="flex actr g10" id="right-toolbar-content">
              <ConcurrentViewers
                concurrentViewers={concurrentViewers}
                className="fx center mlauto g3"
              />
              {ticket.followers?.length > 0 && (
                <div className="ml8 mr8 flex center pointer">
                  <SuperopsTooltip
                    content={<FollowersContent followers={ticket?.followers} />}
                  >
                    <div className="flex center">
                      <SVGIcon name="Follow" size="sm" color="#323232" />
                      <div className="ml8">{`${ticket.followers.length}`}</div>
                    </div>
                  </SuperopsTooltip>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default withErrorBoundary(TicketDetailsHeader);
